import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

// Builder-module SOV builder. Drives the "Build SOV" dropdown:
//   - openModal   → AI extract-from-file (upload → poll → seed grid)
//   - startManual → empty grid
//   - pullDefault → company default SOV template into the grid
// All three seed the reused `sov-editor` grid (review/edit), which POSTs to
// finances#update_project_sov and reloads. AI extraction is the first
// sanctioned AI-on-finance surface (see ai-finance-block policy).
export default class extends Controller {
  static targets = ["modal", "dropzone", "fileInput", "progress", "error", "panel", "categoryModal", "categoryName", "emptyState"]
  static values = {
    presignUrl: String,
    extractUrl: String,
    statusUrl: String,
    defaultUrl: String
  }

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content
    this._polling = null
  }

  disconnect() {
    if (this._polling) clearInterval(this._polling)
    this._stopAiProgress()
  }

  // ── Dropdown options ─────────────────────────────────────────────────────

  openModal() {
    this._resetModal()
    this.modalTarget.classList.remove("hidden")
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
  }

  startManual() {
    this._revealPanel()
    this._editor()?.loadData([{ name: "Category 1", line_items: [] }])
  }

  // ── "+ Category" modal (pick name + kind: line items / materials) ────────

  openCategoryModal() {
    this._revealPanel()
    if (this.hasCategoryNameTarget) this.categoryNameTarget.value = ""
    const lineItem = this.element.querySelector('input[name="build_cat_kind"][value="line_item"]')
    if (lineItem) lineItem.checked = true
    this.categoryModalTarget.classList.remove("hidden")
    if (this.hasCategoryNameTarget) this.categoryNameTarget.focus()
  }

  hideCategoryModal() {
    if (this.hasCategoryModalTarget) this.categoryModalTarget.classList.add("hidden")
  }

  confirmCategory() {
    const name = this.hasCategoryNameTarget ? this.categoryNameTarget.value.trim() : ""
    const kind = this.element.querySelector('input[name="build_cat_kind"]:checked')?.value || "line_item"
    this._editor()?.addNamedCategory(name, kind)
    this.hideCategoryModal()
  }

  async pullDefault() {
    try {
      const resp = await fetch(this.defaultUrlValue, { headers: { Accept: "application/json" } })
      const data = await resp.json()
      const cats = data.categories || []
      if (!cats.length) {
        showToast("No default SOV list found in company settings.", "error")
        return
      }
      this._revealPanel()
      this._editor()?.loadData(cats)
      showToast("Loaded the default SOV list. Review and save.")
    } catch (e) {
      showToast("Couldn't load the default SOV list.", "error")
    }
  }

  // ── Drag & drop / file pick ──────────────────────────────────────────────

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-brand", "bg-blue-50")
  }

  dragleave() {
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50")
    const file = event.dataTransfer?.files?.[0]
    if (file) this._handleFile(file)
  }

  pick() {
    this.fileInputTarget.click()
  }

  fileChosen(event) {
    const file = event.target.files?.[0]
    if (file) this._handleFile(file)
  }

  // ── Upload → extract → poll ───────────────────────────────────────────────

  async _handleFile(file) {
    this._setError("")
    // One continuous banner from the first moment — no quick-flash
    // "Uploading …" line before it.
    this._startAiProgress()
    try {
      const cacheName = await this._presignAndPut(file)
      const id = await this._startExtraction(file, cacheName)
      this._poll(id)
    } catch (e) {
      this._stopAiProgress()
      this._setProgress("")
      this._setError(e.message || "Upload failed. Please try again.")
    }
  }

  // Specs-style AI banner: rainbow "ConsLog AI — …" + elapsed timer, with the
  // stage message advancing as time passes so long reads never look stuck.
  _startAiProgress() {
    if (!this.hasProgressTarget) return
    this._aiStart = performance.now()
    const stages = [
      [0,  "Reading your document..."],
      [8,  "Identifying line items..."],
      [18, "Pulling quantities and prices..."],
      [30, "Structuring categories..."],
      [45, "Still working — bigger file, almost there..."]
    ]
    const render = () => {
      const secs = Math.round((performance.now() - this._aiStart) / 1000)
      let msg = stages[0][1]
      for (const [at, text] of stages) { if (secs >= at) msg = text }
      this.progressTarget.innerHTML =
        `<span class="font-bold ai-rainbow-text">ConsLog AI — ${msg}</span> ` +
        `<span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
    }
    render()
    clearInterval(this._aiTimerInterval)
    this._aiTimerInterval = setInterval(render, 1000)
  }

  _stopAiProgress() {
    clearInterval(this._aiTimerInterval)
    this._aiTimerInterval = null
  }

  async _presignAndPut(file) {
    const presign = await fetch(this.presignUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
      body: JSON.stringify({
        files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }]
      })
    })
    const pj = await presign.json()
    if (!presign.ok) throw new Error(pj.error || "Could not start the upload.")
    const up = pj.uploads[0]
    const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: file })
    if (!put.ok) throw new Error("Upload to storage failed.")
    return up.cache_name
  }

  async _startExtraction(file, cacheName) {
    const resp = await fetch(this.extractUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
      body: JSON.stringify({ cache_name: cacheName, filename: file.name, content_type: file.type })
    })
    const data = await resp.json()
    if (!resp.ok) throw new Error(data.error || "Could not start extraction.")
    return data.id
  }

  _poll(id) {
    const url = `${this.statusUrlValue}?id=${id}`
    this._polling = setInterval(async () => {
      try {
        const resp = await fetch(url, { headers: { Accept: "application/json" } })
        const data = await resp.json()
        if (data.status === "done") {
          clearInterval(this._polling)
          this._stopAiProgress()
          const cats = data.categories || []
          if (!cats.length) {
            this._setProgress("")
            this._setError("We couldn't find any line items in that file.")
            return
          }
          this.hideModal()
          this._revealPanel()
          const editor = this._editor()
          if (editor) {
            // The document's own printed grand total — the grid checks its
            // sum against this and shows a match / mismatch note.
            editor.documentTotal = data.document_total ?? null
            // Notes on anything the AI interpreted/corrected while reading —
            // rendered as an Observations section under the grid.
            editor.observations = Array.isArray(data.observations) ? data.observations : []
            editor.loadData(cats)
          }
          const n = data.line_item_count
          showToast(`Found ${n} line item${n === 1 ? "" : "s"}. Review and save.`)
        } else if (data.status === "failed") {
          clearInterval(this._polling)
          this._stopAiProgress()
          this._setProgress("")
          this._setError(data.error || "Extraction failed. Please try another file.")
        }
      } catch (e) {
        clearInterval(this._polling)
        this._stopAiProgress()
        this._setProgress("")
        this._setError("Lost connection while reading the file.")
      }
    }, 2500)
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _editor() {
    const el = this.element.querySelector('[data-controller~="sov-editor"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "sov-editor") : null
  }

  _revealPanel() {
    if (!this.hasPanelTarget) return
    // The "No Schedule of Values yet" empty state is redundant once the
    // build grid is on screen.
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  _resetModal() {
    this._setError("")
    this._setProgress("")
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
  }

  _setProgress(text) { if (this.hasProgressTarget) this.progressTarget.textContent = text }
  _setError(text) { if (this.hasErrorTarget) this.errorTarget.textContent = text }

  stop(event) { event.stopPropagation() }
}
